import { API } from "apis";
import Image from "components/common/images/Image";
import PageTitle from "components/common/PageTitle";
import PageLabel from "components/common/paging/PageLabel";
import Pagination from "components/common/paging/Pagination2";
import RowsPerPageSelect from "components/common/RowsPerPageSelect";
import ConfirmDialog from "components/dialog/ConfirmDIalog";
import FavoriteItemSearchSortSelect from "components/favorite/item/FavoriteItemSearchSortSelect";
import { PER_PAGE_TYPES } from "enums";
import {
  ContextModel,
  FavoriteItemListModel,
  FavoriteItemModel,
  FavoriteItemSearchOptions,
} from "models";
import qs from "query-string";
import React from "react";
import { Helmet } from "react-helmet-async";
import { HiX } from "react-icons/hi";
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { Utils } from "utils";

const FavoriteItemPage: React.FC = () => {
  const ctx = useOutletContext<ContextModel>();

  const location = useLocation();
  const navigate = useNavigate();
  
  // 直前に検索を行ったクエリ
  const searchedQuery = React.useRef<string>();
  // 検索オプション
  const options = React.useRef<FavoriteItemSearchOptions>({});
  const [data, setData] = React.useState<FavoriteItemListModel>();

  // 削除対象
  const [deleteTarget, setDeleteTarget] =
    React.useState<FavoriteItemModel | null>(null);

  const reload = async () => {
    for (let key in options.current) {
      if ((options.current as any)[key] === "") {
        delete (options.current as any)[key];
      }
    }
    const params = qs.stringify(options.current);
    navigate({ pathname: location.pathname, search: params });
  };

  const onChangeSort = async () => {
    delete options.current.page_no;

    reload();
  };

  const onChangePage = async (value: number) => {
    if (value <= 1) {
      delete options.current.page_no;
    } else {
      options.current.page_no = value;
    }

    reload();
  };

  const onDeleteConfirmed = async (result: boolean) => {
    const target = deleteTarget;
    if (!target) {
      return;
    }
    setDeleteTarget(null);
    if (!result) {
      return;
    }

    ctx.loading.setState(true);
    try {
      const response = await API.FavoriteItem.del(target.favorite_item_cd);
      console.log(response);
      {
        const response = await API.FavoriteItem.search(options.current);
        console.log(response);
        setData(response.data);
      }
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (searchedQuery.current === location.search) {
      console.log("useEffect => load cancel.");
      return;
    }
    console.log("useEffect => load start. : " + location.search);
    searchedQuery.current = location.search;
    const f = async () => {
      ctx.loading.setState(true);
      if (!ctx.auth.data) {
        await ctx.auth.initialize();
      }
      if (!ctx.limitPrice.data) {
        await ctx.limitPrice.load();
      }

      // optionsを一度クリアしてから、クエリの状態に
      for (let k in options.current) {
        delete (options.current as any)[k];
      }
      const query = qs.parse(location.search);
      if (query.page_no !== undefined) {
        options.current.page_no = Number(query.page_no);
      }
      if (query.sort_order !== undefined) {
        options.current.sort_order = String(query.sort_order);
      }
      if (query.get_count !== undefined) {
        options.current.get_count = Number(query.get_count);
      }

      const response = await API.FavoriteItem.search(options.current);
      console.log(response);
      setData(response.data);
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });

      ctx.loading.setState(false);
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (!data) {
    return <div></div>;
  }

  if (data.favorite_item_list.length === 0) {
    return (
      <div>
        <Helmet>
          <title>お気に入り商品一覧</title>
        </Helmet>
        <PageTitle title="お気に入り商品一覧" className="p-2" />
        <div className="flex justify-center py-12">
          <p>お気に入り商品がありません。</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>お気に入り商品一覧</title>
      </Helmet>
      <PageTitle title="お気に入り商品一覧" className="p-2" />

      <div className="m-4">
        <div className="flex justify-between items-center p-1">
          <div>
            <div className="mb-2">
              <RowsPerPageSelect options={options.current} onChange={onChangeSort} />
              <span>を表示</span>
            </div>
            <PageLabel data={data} />
          </div>
          <div>
            <span className="mr-4">並び替え</span>
            <FavoriteItemSearchSortSelect
              options={options.current}
              onChange={onChangeSort}
            />
          </div>
        </div>
        <table className="w-full">
          <thead className="border-b">
            <tr>
              <th className="pl-24 text-left">タイトル</th>
              <th className="text-left">販売会社</th>
              <th className="">価格</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {data.favorite_item_list.map((item, index) => {
              return (
                <tr key={index} className="border-b">
                  <td>
                    <Link to={`/item-detail/${item.favorite_item_cd}/`}>
                      <div className="flex items-center gap-2 group">
                        <div className="w-20 h-20 m-1">
                          <Image
                            data={item.item_main_image}
                            height="5rem"
                            default="item.png"
                          />
                        </div>
                        <span className="flex-1 group-hover:text-jhk-blue group-hover:underline">{item.item_name}</span>
                      </div>
                    </Link>
                  </td>
                  <td className="text-left">
                    <Link
                      to={`/company-detail/${item.sale_corp_id}`}
                      className="hover:underline hover:text-jhk-blue"
                    >
                      {item.sale_corp_name}
                    </Link>
                  </td>
                  <td className="text-right">
                    {item.is_discretionary_price ? (
                      <>-</>
                    ) : (
                      <div className="whitespace-nowrap">
                        {Utils.addComma(item.sale_price)}円
                      </div>
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      className="light-gray p-2 text-2xl border"
                      onClick={() => setDeleteTarget(item)}
                    >
                      <HiX />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex justify-center m-4">
          <Pagination
            page={options.current?.page_no ?? 1}
            data={data}
            onChange={onChangePage}
          />
        </div>
      </div>
      <ConfirmDialog
        modalIsOpen={!!deleteTarget}
        message="お気に入り商品を削除します。よろしいですか？"
        action={onDeleteConfirmed}
      />
    </div>
  );
};
export default FavoriteItemPage;
